import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { GLOBE_RADIUS, latLonToVector3, magnitudeToSpread, POP_CENTERS } from "../lib/seismic";
import { useSim } from "../lib/store";

function softDot() {
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.3, "rgba(255,255,255,0.7)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(c);
}

const CYAN = new THREE.Color("#06d6f0");
const WHITE = new THREE.Color("#ffffff");
const RED = new THREE.Color("#ff2a2a");

export function PopulationClusters() {
  const pointsRef = useRef();
  const epicenter = useSim((s) => s.epicenter);
  const tex = useMemo(() => softDot(), []);

  const { geometry, dists, colors, base } = useMemo(() => {
    const n = POP_CENTERS.length;
    const positions = new Float32Array(n * 3);
    const colors = new Float32Array(n * 3);
    const base = new Float32Array(n * 3);
    POP_CENTERS.forEach((c, i) => {
      const p = latLonToVector3(c.lat, c.lon, GLOBE_RADIUS + 0.02);
      positions[i * 3] = p.x; positions[i * 3 + 1] = p.y; positions[i * 3 + 2] = p.z;
      colors[i * 3] = CYAN.r; colors[i * 3 + 1] = CYAN.g; colors[i * 3 + 2] = CYAN.b;
      base[i * 3] = p.x; base[i * 3 + 1] = p.y; base[i * 3 + 2] = p.z;
    });
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geom.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    return { geometry: geom, dists: new Float32Array(n), colors, base };
  }, []);

  useEffect(() => {
    if (!epicenter) return;
    const epi = latLonToVector3(epicenter.lat, epicenter.lon, 1).normalize();
    POP_CENTERS.forEach((c, i) => {
      const u = new THREE.Vector3(base[i * 3], base[i * 3 + 1], base[i * 3 + 2]).normalize();
      dists[i] = Math.acos(THREE.MathUtils.clamp(u.dot(epi), -1, 1));
    });
  }, [epicenter, base, dists]);

  useFrame((state) => {
    const st = useSim.getState();
    const attr = geometry.getAttribute("color");
    const t = state.clock.elapsedTime;
    let front = -1;
    if (st.simulating && st.epicenter) {
      const smoothP = Math.min((performance.now() - st.simStart) / 1000 / st.duration, 1);
      front = magnitudeToSpread(st.magnitude) * (0.12 + 0.95 * smoothP);
    }
    const tmp = new THREE.Color();
    for (let i = 0; i < POP_CENTERS.length; i++) {
      if (front >= 0 && dists[i] <= front) {
        // hit: flicker white/red
        const flick = 0.5 + 0.5 * Math.sin(t * 22 + i);
        tmp.copy(RED).lerp(WHITE, flick * 0.6);
      } else {
        // idle soft cyan pulse
        const pulse = 0.7 + 0.3 * Math.sin(t * 1.5 + i);
        tmp.copy(CYAN).multiplyScalar(pulse);
      }
      attr.array[i * 3] = tmp.r; attr.array[i * 3 + 1] = tmp.g; attr.array[i * 3 + 2] = tmp.b;
    }
    attr.needsUpdate = true;
  });

  return (
    <points ref={pointsRef} geometry={geometry} frustumCulled={false}>
      <pointsMaterial
        map={tex}
        size={0.22}
        sizeAttenuation
        vertexColors
        transparent
        opacity={0.9}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        toneMapped={false}
      />
    </points>
  );
}
